/**
 * File detail: hangi versiyonların listelenip seçileceği (anonim + Guest paylaşımı).
 * API modelleri generate; bu dosya sadece client tarafı filtreleme.
 */

import { getApiUtcTimeMs } from './apiDateTime';

export function pickLatestVersion(fileVersions: any[]): any | null {
  if (!fileVersions || fileVersions.length === 0) return null;
  try {
    return fileVersions.reduce((acc: any, cur: any) => {
      const accDate = acc?.createdAt ? getApiUtcTimeMs(acc.createdAt) : 0;
      const curDate = cur?.createdAt ? getApiUtcTimeMs(cur.createdAt) : 0;
      if (curDate === accDate) {
        return (cur?.id || 0) > (acc?.id || 0) ? cur : acc;
      }
      return curDate > accDate ? cur : acc;
    }, fileVersions[0]);
  } catch {
    return fileVersions[fileVersions.length - 1] || fileVersions[0];
  }
}

/** URL hash (ör. v3, V3) → versiyon; FileDetail’deki eşleme mantığıyla uyumlu */
export function findVersionByHash(fileVersions: any[], hash: string): any | null {
  if (!hash || !fileVersions?.length) return null;
  const h = hash.trim();
  if (!h) return null;

  let target = fileVersions.find(
    (v: any) => v.name && v.name.toLowerCase() === h.toLowerCase()
  );
  if (target) return target;

  target = fileVersions.find((v: any) => {
    if (v.name) {
      const versionNumber = h.toLowerCase().replace('v', '');
      const versionName = v.name.toLowerCase().replace('v', '');
      return versionName === versionNumber || v.name.toLowerCase() === h.toLowerCase();
    }
    return false;
  });
  if (target) return target;

  target = fileVersions.find((v: any) => {
    if (v.name) {
      return (
        v.name.toLowerCase().includes(h.toLowerCase()) ||
        h.toLowerCase().includes(v.name.toLowerCase())
      );
    }
    return false;
  });
  if (target) return target;

  return (
    fileVersions.find((v: any) => {
      if (v.path) {
        return v.path.toLowerCase().includes(h.toLowerCase());
      }
      return false;
    }) || null
  );
}

export function isUserInVersionShare(
  user: { id?: number; email?: string | null } | null | undefined,
  version: any
): boolean {
  if (!user) return false;
  const list = version?.share;
  if (!Array.isArray(list) || list.length === 0) return false;
  const uid = user.id;
  const email = user.email != null ? String(user.email).trim().toLowerCase() : '';
  return list.some((s: any) => {
    if (uid != null && s?.id != null && Number(s.id) === Number(uid)) return true;
    if (email && s?.email && String(s.email).trim().toLowerCase() === email) return true;
    return false;
  });
}

function guestAllowedVersions(fileVersions: any[], user: any): any[] {
  return fileVersions.filter((v) => isUserInVersionShare(user, v));
}

export type FileDetailVersionViewer = {
  user: any | undefined;
  isGuest: boolean;
  isAnonymous: boolean;
};

export function getVisibleVersionsForViewer(
  fileVersions: any[],
  viewer: FileDetailVersionViewer,
  hash: string
): { visibleVersions: any[]; selectedVersionId: number } {
  const { user, isGuest, isAnonymous } = viewer;
  const latest = pickLatestVersion(fileVersions) || fileVersions[0];
  const latestId = latest?.id ?? 1;

  if (isAnonymous) {
    if (hash) {
      const match = findVersionByHash(fileVersions, hash);
      if (match) {
        return { visibleVersions: [match], selectedVersionId: match.id };
      }
      return {
        visibleVersions: latest ? [latest] : [],
        selectedVersionId: latestId
      };
    }
    return {
      visibleVersions: latest ? [latest] : [],
      selectedVersionId: latestId
    };
  }

  if (isGuest && user) {
    const allowed = guestAllowedVersions(fileVersions, user);
    if (allowed.length === 0) {
      return { visibleVersions: [], selectedVersionId: latestId };
    }
    let selectedId: number;
    if (hash) {
      const fromHash = findVersionByHash(allowed, hash);
      if (fromHash) {
        selectedId = fromHash.id;
      } else {
        const best = pickLatestVersion(allowed) || allowed[0];
        selectedId = best.id;
      }
    } else {
      const best = pickLatestVersion(allowed) || allowed[0];
      selectedId = best.id;
    }
    return { visibleVersions: allowed, selectedVersionId: selectedId };
  }

  let selectedId = latestId;
  if (hash) {
    const match = findVersionByHash(fileVersions, hash);
    if (match) selectedId = match.id;
  }
  return { visibleVersions: fileVersions, selectedVersionId: selectedId };
}

/** /versions ızgarası: aynı görünürlük kuralları, sadece dizi */
export function listVisibleVersionsForFileDetail(
  fileVersions: any[],
  viewer: FileDetailVersionViewer,
  hash: string
): any[] {
  return getVisibleVersionsForViewer(fileVersions, viewer, hash).visibleVersions;
}
